import { FC, ReactNode, useCallback } from 'react'
import { generatePath, Link, useNavigate } from 'react-router'

import { Avatar, Card, Divider, Flex, Tag, theme, Typography } from 'antd'

import dayjs from 'dayjs'

import routes from '../../routes'

type AuthorMetaProps = {
  avatar: string
  initials?: string
  title: ReactNode
  description: string
}
const { useToken } = theme

const AuthorMeta: FC<Partial<AuthorMetaProps>> = ({ avatar, title, initials, description = '' }) => {
  return (
    <Flex gap={8} align={'center'} style={{ minWidth: 160 }}>
      <Avatar alt={typeof title === 'string' ? title : initials} size={48} src={initials ? undefined : avatar}>
        {initials?.toUpperCase()}
      </Avatar>
      <Flex vertical>
        {typeof title === 'string' ? <Typography.Text>{title}</Typography.Text> : title}
        <Typography.Text>{description}</Typography.Text>
      </Flex>
    </Flex>
  )
}

const CardMeta: FC<Partial<Pick<ArticleCardFragment, 'title' | 'teaser' | 'slug'>>> = ({
  teaser,
  title,
  slug = '',
}) => {
  const { token } = useToken()
  return (
    <Flex vertical gap={8}>
      <Typography.Title
        style={{ minHeight: token.fontSize * token.lineHeight * 2 + 4, marginBottom: 0 }}
        ellipsis={{ rows: 2 }}
        level={5}
      >
        <Link to={generatePath(routes.article, { slug })}>{title}</Link>
      </Typography.Title>
      <Typography.Paragraph
        ellipsis={{ rows: 3 }}
        style={{ minHeight: token.fontSize * token.lineHeight * 3, marginBottom: 8 }}
      >
        {teaser}
      </Typography.Paragraph>
    </Flex>
  )
}

const TagsMeta: FC<{ tags: Partial<TagFragment | null | undefined>[] }> = ({ tags }) => {
  const { token } = useToken()
  return tags.length > 0 ? (
    <Flex gap={8}>
      {tags?.map(
        (it) =>
          it && (
            <Tag key={it?.documentId} color={'error'}>
              {it?.slug ? (
                <Link style={{ color: token.colorError }} to={generatePath(routes.tag, { slug: it?.slug })}>
                  {it?.name}
                </Link>
              ) : null}
            </Tag>
          ),
      )}
    </Flex>
  ) : (
    <div style={{ minHeight: 24 }} />
  )
}

const ArticleCard: FC<Partial<ArticleCardFragment> & { tags?: (Partial<TagFragment | undefined> | null)[] }> = ({
  ...props
}) => {
  const navigate = useNavigate()
  const onClick = useCallback(
    () => props.slug && navigate(generatePath(routes.article, { slug: props.slug })),
    [props.slug, navigate],
  )

  return (
    <Card
      styles={{
        body: { padding: '0 16px 16px' },
      }}
      cover={
        <img
          alt={props.cover?.alternativeText ?? props.title}
          onClick={onClick}
          style={{ objectFit: 'cover' }}
          height={240}
          src={`${props.cover?.formats?.small?.url}`}
        />
      }
    >
      <Flex style={{ position: 'relative', top: -40, zIndex: 2 }} gap={4}>
        <Tag color={'volcano-inverse'} bordered>
          {props.category?.name}
        </Tag>
      </Flex>
      <Flex vertical gap={8}>
        <CardMeta teaser={props.teaser} title={props.title} slug={props.slug} />
        <AuthorMeta
          avatar={props.author?.avatar?.url}
          title={props.author?.name}
          description={dayjs(props.updatedAt).format('DD MMMM, HH:mm')}
        />
      </Flex>
      <Divider style={{ margin: '16px 0' }} />
      <TagsMeta tags={props.tags ?? []} />
    </Card>
  )
}

export { ArticleCard, AuthorMeta }
